import React, { useEffect, useMemo, useRef, useState, useId } from 'react';

/**
 * <Icon.Metallic />
 * Wrap any Lucide icon and render it with a metallic gradient stroke.
 *
 * Props:
 *  - Icon:        React component from lucide-react (required)
 *  - size:        number (default 24)
 *  - variant:     "silver" | "gold" | "roseGold" | "bronze" | "titanium" | "emerald" | "platinum" | "copper" | "amethyst" | "sapphire" | "ruby" | "obsidian" | "aqua" | "sunset"
 *  - rotateColors:boolean (cycle through variants)
 *  - intervalMs:  number (default 2500)
 *  - strokeWidth: number (default 2)
 *  - stops:       custom gradient stops [{offset, color}]
 *  - gradientDir: { x1, y1, x2, y2 } defaults 0,0 → 100,100
 *  - ...rest:     forwarded to Icon
 */
export const MetallicIcon = ({
  Icon,
  size = 24,
  variant = 'silver',
  rotateColors = false,
  intervalMs = 2500,
  strokeWidth = 2,
  stops,
  gradientDir,
  ...rest
}) => {
  const id = useId();
  const prefersReduced = usePrefersReducedMotion();

  const VARIANTS = useMemo(
    () => ({
      silver: [
        { offset: '0%',   color: 'var(--ng-metal-silver-0, #f8f9fa)' },
        { offset: '35%',  color: 'var(--ng-metal-silver-1, #c0c0c0)' },
        { offset: '65%',  color: 'var(--ng-metal-silver-2, #9aa0a6)' },
        { offset: '100%', color: 'var(--ng-metal-silver-3, #e6e6e6)' }
      ],
      gold: [
        { offset: '0%',   color: 'var(--ng-metal-gold-0, #fff7cc)' },
        { offset: '35%',  color: 'var(--ng-metal-gold-1, #ffd54a)' },
        { offset: '65%',  color: 'var(--ng-metal-gold-2, #e6b800)' },
        { offset: '100%', color: 'var(--ng-metal-gold-3, #fff0a3)' }
      ],
      roseGold: [
        { offset: '0%',   color: 'var(--ng-metal-rose-0, #ffe4e1)' },
        { offset: '35%',  color: 'var(--ng-metal-rose-1, #f4a6a1)' },
        { offset: '65%',  color: 'var(--ng-metal-rose-2, #d88983)' },
        { offset: '100%', color: 'var(--ng-metal-rose-3, #ffd1cc)' }
      ],
      bronze: [
        { offset: '0%',   color: 'var(--ng-metal-bronze-0, #f1e0d0)' },
        { offset: '35%',  color: 'var(--ng-metal-bronze-1, #cd7f32)' },
        { offset: '65%',  color: 'var(--ng-metal-bronze-2, #91572a)' },
        { offset: '100%', color: 'var(--ng-metal-bronze-3, #e8c7a8)' }
      ],
      titanium: [
        { offset: '0%',   color: 'var(--ng-metal-ti-0, #e8eef7)' },
        { offset: '35%',  color: 'var(--ng-metal-ti-1, #7aa2d2)' },
        { offset: '65%',  color: 'var(--ng-metal-ti-2, #4b6b9b)' },
        { offset: '100%', color: 'var(--ng-metal-ti-3, #c8d6ec)' }
      ],
      emerald: [
        { offset: '0%',   color: 'var(--ng-metal-em-0, #e6fff6)' },
        { offset: '35%',  color: 'var(--ng-metal-em-1, #34d399)' },
        { offset: '65%',  color: 'var(--ng-metal-em-2, #0f766e)' },
        { offset: '100%', color: 'var(--ng-metal-em-3, #b2f5ea)' }
      ],
      platinum: [
        { offset: '0%',   color: 'var(--ng-metal-platinum-0, #fdfdfd)' },
        { offset: '35%',  color: 'var(--ng-metal-platinum-1, #e6e6e6)' },
        { offset: '65%',  color: 'var(--ng-metal-platinum-2, #c9d6df)' },
        { offset: '100%', color: 'var(--ng-metal-platinum-3, #f2f2f2)' }
      ],
      copper: [
        { offset: '0%',   color: 'var(--ng-metal-copper-0, #ffe0cc)' },
        { offset: '35%',  color: 'var(--ng-metal-copper-1, #d77b42)' },
        { offset: '65%',  color: 'var(--ng-metal-copper-2, #994d25)' },
        { offset: '100%', color: 'var(--ng-metal-copper-3, #ffb899)' }
      ],
      amethyst: [
        { offset: '0%',   color: 'var(--ng-metal-amethyst-0, #f4e9ff)' },
        { offset: '35%',  color: 'var(--ng-metal-amethyst-1, #b388ff)' },
        { offset: '70%',  color: 'var(--ng-metal-amethyst-2, #7c4dff)' },
        { offset: '100%', color: 'var(--ng-metal-amethyst-3, #e1cfff)' }
      ],
      sapphire: [
        { offset: '0%',   color: 'var(--ng-metal-sapphire-0, #dbeafe)' },
        { offset: '35%',  color: 'var(--ng-metal-sapphire-1, #3b82f6)' },
        { offset: '65%',  color: 'var(--ng-metal-sapphire-2, #1e40af)' },
        { offset: '100%', color: 'var(--ng-metal-sapphire-3, #93c5fd)' }
      ],
      ruby: [
        { offset: '0%',   color: 'var(--ng-metal-ruby-0, #fee2e2)' },
        { offset: '35%',  color: 'var(--ng-metal-ruby-1, #ef4444)' },
        { offset: '65%',  color: 'var(--ng-metal-ruby-2, #991b1b)' },
        { offset: '100%', color: 'var(--ng-metal-ruby-3, #fca5a5)' }
      ],
      obsidian: [
        { offset: '0%',   color: 'var(--ng-metal-obsidian-0, #111827)' },
        { offset: '35%',  color: 'var(--ng-metal-obsidian-1, #1f2937)' },
        { offset: '65%',  color: 'var(--ng-metal-obsidian-2, #374151)' },
        { offset: '100%', color: 'var(--ng-metal-obsidian-3, #4b5563)' }
      ],
      aqua: [
        { offset: '0%',   color: 'var(--ng-metal-aqua-0, #ecfeff)' },
        { offset: '35%',  color: 'var(--ng-metal-aqua-1, #22d3ee)' },
        { offset: '65%',  color: 'var(--ng-metal-aqua-2, #0e7490)' },
        { offset: '100%', color: 'var(--ng-metal-aqua-3, #67e8f9)' }
      ],
      sunset: [
        { offset: '0%',   color: 'var(--ng-metal-sunset-0, #ffedd5)' },
        { offset: '35%',  color: 'var(--ng-metal-sunset-1, #fb923c)' },
        { offset: '65%',  color: 'var(--ng-metal-sunset-2, #db2777)' },
        { offset: '100%', color: 'var(--ng-metal-sunset-3, #fda4af)' }
      ]
    }),
    []
  );

  const order = useMemo(() => Object.keys(VARIANTS), [VARIANTS]);
  const [active, setActive] = useState(variant);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!rotateColors || prefersReduced) return;
    let i = Math.max(order.indexOf(variant), 0);
    timerRef.current = setInterval(() => {
      i = (i + 1) % order.length;
      setActive(order[i]);
    }, intervalMs);
    return () => clearInterval(timerRef.current);
  }, [rotateColors, intervalMs, prefersReduced, order, variant]);

  const gradientStops = stops || VARIANTS[active] || VARIANTS.silver;
  const dir = {
    x1: gradientDir?.x1 ?? '0%',
    y1: gradientDir?.y1 ?? '0%',
    x2: gradientDir?.x2 ?? '100%',
    y2: gradientDir?.y2 ?? '100%'
  };

  // Inject the gradient <defs> into the Lucide icon's own <svg> element so the
  // url(#id) paint server resolves within the same SVG tree (no nested SVG issue).
  const base = <Icon {...rest} size={size} stroke={`url(#metal-${id})`} strokeWidth={strokeWidth} />;
  if (!React.isValidElement(base)) return null;
  const children = base.props.children;
  const withDefs = (
    <>
      <defs>
        <linearGradient id={`metal-${id}`} {...dir}>
          {gradientStops.map((s, idx) => (
            <stop key={idx} offset={s.offset} stopColor={s.color} />
          ))}
        </linearGradient>
      </defs>
      {children}
    </>
  );

  return React.cloneElement(
    base,
    {
      role: 'img',
      'aria-hidden': rest['aria-label'] ? undefined : true,
    },
    withDefs,
  );
};

/* utilities */
function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    const onChange = () => setReduced(!!mq?.matches);
    onChange();
    mq?.addEventListener?.('change', onChange);
    return () => mq?.removeEventListener?.('change', onChange);
  }, []);
  return reduced;
}
